import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { adminViews, workforce } from '@cwos/api-client';
import type { LocationVerification } from '@cwos/ui-shared';
import {
  ErrorPanel,
  Pill,
  Refused,
  SkeletonSet,
  SuccessEmpty,
  VerificationBadge,
} from '@cwos/ui-admin';
import { usePermissions } from '../permissions.js';
import { useNav } from '../navigation.js';
import { ListState } from '../ListStates.js';
import { listPhase, type ListPhase } from '../listState.js';
import {
  AT_SITE_STATES,
  UNVERIFIED_STATES,
  formatDistance,
  formatClock,
  formatDay,
  splitVerification,
  verificationScopeNote,
  exceptionScopeNote,
} from './timeLogic.js';
import { browserTimezone } from '../workforce/workforceLogic.js';
import { localDayString } from '../../lib/timeFormat.js';
import { LoadFailure } from '../LoadFailure.js';

/**
 * Time exceptions — the queue behind the tallies.
 *
 * The old Verification screen printed "17,277 check-ins · 16,276 at a work site ·
 * 1,001 away" and stopped there. Three dead numbers on an operational surface is
 * worse than none: they raise a question the product then refuses to answer. Every
 * count on this screen opens the records underneath it.
 *
 * WORDING. The second bucket is "Needs a look", never "Away" and never "Fraud".
 * It contains punches recorded outside a geofence, punches with no geofence to
 * check against, punches where GPS was unavailable or denied, and punches the
 * worker attested to. Only one of those is even arguably a person's fault, and a
 * queue label that presumes otherwise is how a support tool becomes a
 * surveillance tool.
 */

type Bucket = 'all' | 'at_site' | 'needs_look';

const BUCKET_LABELS: Record<Bucket, string> = {
  all: 'All check-ins',
  at_site: 'At a work site',
  needs_look: 'Needs a look',
};

const KNOWN_VERIFICATION = new Set<string>([...AT_SITE_STATES, ...UNVERIFIED_STATES]);

/** What each verification state actually means, in a reviewer's words. */
const STATE_MEANING: Record<string, string> = {
  verified_inside: 'Recorded inside a work site’s geofence.',
  verified_outside: 'Recorded outside every work-site geofence.',
  recorded_no_geofence: 'No work site was configured to check this against.',
  low_accuracy: 'A position arrived, but too imprecise to place.',
  unavailable_no_fix: 'The device could not get a position.',
  unavailable_permission_denied: 'The worker’s device denied location access.',
  unavailable_policy_off: 'Location collection is turned off by policy.',
  attested_by_worker: 'The worker confirmed where they were, without a position.',
};

export function TimeExceptions() {
  const navigate = useNav();
  const { has } = usePermissions();
  const initial = (new URLSearchParams(window.location.search).get('bucket') as Bucket) || 'all';
  const [bucket, setBucket] = useState<Bucket>(
    initial === 'at_site' || initial === 'needs_look' ? initial : 'all',
  );

  const verification = useQuery({
    queryKey: ['admin-views', 'verification'],
    queryFn: () => adminViews.verification(),
  });
  const board = useQuery({
    queryKey: ['admin-views', 'workforce'],
    queryFn: () => adminViews.workforce(),
  });
  const exceptions = useQuery({
    queryKey: ['admin-views', 'exceptions'],
    queryFn: () => adminViews.exceptions({ pageSize: 50 }),
  });

  const split = splitVerification(verification.data);

  if (verification.isLoading) return <SkeletonSet surface="table" rows={6} />;
  if (verification.isError && !verification.data) {
    return (
      <LoadFailure
        error={verification.error}
        message="We couldn’t load check-in verification just now."
        onRetry={() => void verification.refetch()}
      />
    );
  }

  const states = (verification.data?.byState ?? []).filter((s) => {
    if (bucket === 'at_site') return AT_SITE_STATES.has(s.state);
    if (bucket === 'needs_look') return UNVERIFIED_STATES.has(s.state);
    return true;
  });

  return (
    <div className="rn-col">
      <section className="rn-card">
        <div className="rn-card-head">
          <h2 className="rn-card-title">How check-ins verified</h2>
          <label className="cmp-filter">
            <span className="rn-muted">Show</span>
            <select
              value={bucket}
              onChange={(e) => setBucket(e.target.value as Bucket)}
              aria-label="Filter check-ins"
            >
              {(Object.keys(BUCKET_LABELS) as Bucket[]).map((b) => (
                <option key={b} value={b}>
                  {BUCKET_LABELS[b]}
                </option>
              ))}
            </select>
          </label>
        </div>

        {/* Whose check-ins these are, present only when narrower than the
            organisation. See `verificationScopeNote`. */}
        {verificationScopeNote(verification.data) ? (
          <p className="rn-muted" style={{ marginTop: 'var(--space-6)' }}>
            {verificationScopeNote(verification.data)}
          </p>
        ) : null}

        {split.total === 0 ? (
          <SuccessEmpty
            heading="Nothing recorded yet"
            message="No check-ins have been recorded to review."
          />
        ) : (
          <>
            <div className="tc-kpis" style={{ marginTop: 'var(--space-12)' }}>
              <button
                type="button"
                className="db-kpi tc-kpi-btn"
                onClick={() => setBucket('at_site')}
              >
                <span className="db-kpi-label">At a work site</span>
                <span className="db-kpi-value">{split.atSite}</span>
                <span className="db-kpi-foot">
                  <span className="rn-muted">Verified inside a geofence</span>
                </span>
              </button>
              <button
                type="button"
                className="db-kpi tc-kpi-btn"
                onClick={() => setBucket('needs_look')}
              >
                <span className="db-kpi-label">Needs a look</span>
                <span className="db-kpi-value">{split.needsLook}</span>
                <span className="db-kpi-foot">
                  <span className="rn-muted">Outside, unverified or unavailable</span>
                </span>
              </button>
            </div>

            {/* The states themselves, each with what it means. A reviewer
                deciding whether to chase somebody needs to know that
                "permission denied" is a device setting, not a choice to hide. */}
            <ul className="cmp-controls" style={{ marginTop: 'var(--space-16)' }}>
              {states.map((s) => (
                <li key={s.state} className="cmp-control">
                  <div className="cmp-control-head">
                    {KNOWN_VERIFICATION.has(s.state) ? (
                      <VerificationBadge verification={s.state as LocationVerification} />
                    ) : (
                      <span className="cmp-control-title">{s.state}</span>
                    )}
                    <span className="tc-elapsed">{s.count}</span>
                  </div>
                  <p className="rn-muted cmp-control-desc">
                    {STATE_MEANING[s.state] ?? 'Recorded with this verification state.'}
                  </p>
                </li>
              ))}
            </ul>
          </>
        )}
      </section>

      {/* Each of these three used to receive `q.data ?? []` and a `loading`
          flag, and nothing else. So a failed `exceptions` read said "No open
          exceptions", and a failed `board` read made the two sections below it
          disappear from the page entirely. Both are answers this screen was not
          entitled to give. The queries go in whole now. */}
      <OpenExceptionQueue
        items={exceptions.data?.items ?? []}
        scopeNote={exceptionScopeNote(exceptions.data)}
        phase={listPhase(
          exceptions,
          (exceptions.data?.items ?? []).filter((i) => i.status !== 'resolved').length,
        )}
        onRetry={() => void exceptions.refetch()}
        onOpen={(sessionId) => navigate(`/sessions/${sessionId}`)}
      />

      <MissingClockOuts
        rows={board.data?.missingClockOuts ?? []}
        phase={listPhase(board, (board.data?.missingClockOuts ?? []).length)}
        onRetry={() => void board.refetch()}
        onOpen={(sessionId) => navigate(`/sessions/${sessionId}`)}
      />

      <NoShows
        rows={board.data?.unscheduledNoShows ?? []}
        phase={listPhase(board, (board.data?.unscheduledNoShows ?? []).length)}
        onRetry={() => void board.refetch()}
      />

      {has('location_history:view') ? <AwayFromSite /> : null}
    </div>
  );
}

/**
 * The session-exception queue: gaps, overtime, no-shows and clock-out problems
 * the system detected, each opening the session it came from.
 */
function OpenExceptionQueue({
  items,
  scopeNote,
  phase,
  onRetry,
  onOpen,
}: {
  items: readonly adminViews.ExceptionPanelItem[];
  /** Whose queue this is, when it is narrower than the organisation. */
  scopeNote: string | null;
  phase: ListPhase;
  onRetry: () => void;
  onOpen: (sessionId: string) => void;
}) {
  const open = items.filter((i) => i.status !== 'resolved');
  return (
    <section className="rn-card">
      <h2 className="rn-card-title">Open exceptions</h2>
      {/* Rendered ABOVE the list, so it frames the empty state too: "No open
          exceptions" reads as a statement about the organisation otherwise. */}
      {scopeNote ? (
        <p className="rn-muted" style={{ marginTop: 'var(--space-6)' }}>
          {scopeNote}
        </p>
      ) : null}
      <ListState
        phase={phase}
        rows={4}
        onRetry={onRetry}
        failedMessage="We couldn’t load the exception queue just now. This is not the same as there being none."
        empty={
          <SuccessEmpty
            heading="No open exceptions"
            message="Detected problems with sessions appear here for review."
          />
        }
      >
        <ul className="tc-rows">
          {open.map((item) => (
            <li key={item.id} className="tc-row">
              <button
                type="button"
                className="tc-row-btn"
                disabled={!item.sessionId}
                onClick={() => item.sessionId && onOpen(item.sessionId)}
              >
                <span className="tc-row-main">
                  <span className="tc-row-name">{exceptionLabel(item.kind)}</span>
                  <span className="rn-muted">
                    {item.workerDisplayName} · {formatDay(item.createdAt)}
                  </span>
                </span>
                <span className="tc-row-side">
                  <Pill variant={item.severity === 'warning' ? 'look' : 'unsure'} dot>
                    Needs review
                  </Pill>
                </span>
              </button>
            </li>
          ))}
        </ul>
      </ListState>
    </section>
  );
}

function exceptionLabel(kind: string): string {
  switch (kind) {
    case 'missing_clock_out':
      return 'A session was never clocked out';
    case 'breadcrumb_gap':
      return 'The location trail has a gap';
    case 'location_gap':
      return 'Location was unavailable for part of a session';
    case 'overtime':
      return 'A session ran unusually long';
    case 'no_show':
      return 'A scheduled shift had no session';
    default:
      return 'An exception was recorded';
  }
}

/**
 * A section that is absent when there is nothing to show is fine. A section
 * that is absent because the request failed is not — the reader cannot tell the
 * difference between "no session was left open" and "we never found out", and
 * one of those is a payroll problem. Empty still returns null; failed says so.
 */
function MissingClockOuts({
  rows,
  phase,
  onRetry,
  onOpen,
}: {
  rows: readonly adminViews.MissingClockOutEntry[];
  phase: ListPhase;
  onRetry: () => void;
  onOpen: (sessionId: string) => void;
}) {
  if (phase.kind === 'loading' || phase.kind === 'empty') return null;
  if (phase.kind === 'failed' || phase.kind === 'refused') {
    return (
      <section className="rn-card">
        <h2 className="rn-card-title">Sessions still open</h2>
        {phase.kind === 'refused' ? (
          <Refused message="Your account cannot see the organisation-wide clock, so open sessions are not listed here." />
        ) : (
          <ErrorPanel
            message="We couldn’t check for sessions that were never clocked out. Treat this as unknown, not as none."
            onRetry={onRetry}
          />
        )}
      </section>
    );
  }
  if (rows.length === 0) return null;
  return (
    <section className="rn-card">
      <h2 className="rn-card-title">Sessions still open</h2>
      {/* "Hours unknown" not "0 hours" — the difference between a record that
          is missing and a day somebody did not work. */}
      <p className="rn-muted" style={{ marginTop: 'var(--space-6)' }}>
        These sessions have no clock-out, so their hours are unknown rather than zero.
      </p>
      <ul className="tc-rows">
        {rows.map((r) => (
          <li key={r.sessionId} className="tc-row">
            <button type="button" className="tc-row-btn" onClick={() => onOpen(r.sessionId)}>
              <span className="tc-row-main">
                <span className="tc-row-name">{r.displayName}</span>
                <span className="rn-muted">
                  Clocked in {formatClock(r.clockedInAt)} on {formatDay(r.clockedInAt)}
                </span>
              </span>
              <span className="tc-row-side">
                <span className="tc-elapsed">{Math.round(r.openDurationMinutes / 60)}h open</span>
                <Pill variant="urgent" dot>
                  Needs a clock-out
                </Pill>
              </span>
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}

function NoShows({
  rows,
  phase,
  onRetry,
}: {
  rows: readonly adminViews.UnscheduledNoShowEntry[];
  phase: ListPhase;
  onRetry: () => void;
}) {
  if (phase.kind === 'loading' || phase.kind === 'empty') return null;
  if (phase.kind === 'failed' || phase.kind === 'refused') {
    return (
      <section className="rn-card">
        <h2 className="rn-card-title">Scheduled, but not clocked in</h2>
        {phase.kind === 'refused' ? (
          <Refused message="Your account cannot see the organisation-wide clock, so missed work is not listed here." />
        ) : (
          <ErrorPanel
            message="We couldn’t check which scheduled work has no session yet."
            onRetry={onRetry}
          />
        )}
      </section>
    );
  }
  if (rows.length === 0) return null;
  return (
    <section className="rn-card">
      <h2 className="rn-card-title">Scheduled, but not clocked in</h2>
      <ul className="tc-rows">
        {rows.map((r) => (
          <li key={`${r.kind}:${r.plannedWorkId}`} className="tc-row">
            <div className="tc-row-btn" style={{ cursor: 'default' }}>
              <span className="tc-row-main">
                <span className="tc-row-name">{r.displayName}</span>
                <span className="rn-muted">
                  {r.kind === 'visit' ? 'Visit' : 'Shift'} started {formatClock(r.startsAt)} ·{' '}
                  {r.minutesPastStart} minutes ago
                </span>
              </span>
              <span className="tc-row-side">
                <Pill variant="look" dot>
                  No session yet
                </Pill>
              </span>
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}

/**
 * Punches recorded away from an expected site, with the distance.
 *
 * Gated on `location_history:view` — the distance between a punch and a work
 * site is location data about a person, and an exception queue is not a way
 * around the permission that governs it.
 */
function AwayFromSite() {
  const navigate = useNav();
  // Local calendar day, not the UTC date (`toISOString().slice(0,10)`): the
  // UTC date rolls to tomorrow around 19:00 local, so the "outside a work
  // site" queue silently read an empty next day every evening.
  const today = useMemo(() => localDayString(new Date()), []);
  const map = useQuery({
    queryKey: ['workforce', 'map', today],
    queryFn: () =>
      workforce.workforceMap(today, {
        tz: browserTimezone(),
        reason: 'Time clock exceptions: reviewing check-ins recorded outside a work site',
      }),
  });

  const outside = (map.data?.workers ?? []).filter(
    (w) => w.lastLocation && w.lastLocation.geofenceStatus === 'outside',
  );

  return (
    <section className="rn-card">
      <h2 className="rn-card-title">Last position outside a work site</h2>
      <p className="rn-muted" style={{ marginTop: 'var(--space-6)' }}>
        Being outside a geofence is not by itself a problem — community work happens in the
        community. This list exists so a supervisor can check, not accuse.
      </p>
      {/* "Nobody's last position was outside a work site" was being rendered
          from a failed map read. That is a statement about where people were,
          made without having asked — and this screen sits one click from a
          supervisor's judgement about somebody's day. */}
      <ListState
        phase={listPhase(map, outside.length)}
        rows={4}
        onRetry={() => void map.refetch()}
        failedMessage="We couldn’t read positions just now, so we cannot say whether anyone was outside a work site."
        refused={
          <Refused message="Your account cannot read location history, so positions are not shown here." />
        }
        empty={
          <SuccessEmpty
            heading="Nobody’s last position was outside a work site"
            message="Positions recorded inside a geofence are not listed here."
          />
        }
      >
        <ul className="tc-rows">
          {outside.map((w) => {
            const open = w.sessions.find((s) => s.openNow) ?? w.sessions[0];
            return (
              <li key={w.workerId} className="tc-row">
                <button
                  type="button"
                  className="tc-row-btn"
                  disabled={!open}
                  onClick={() => open && navigate(`/sessions/${open.sessionId}`)}
                >
                  <span className="tc-row-main">
                    <span className="tc-row-name">{w.displayName}</span>
                    <span className="rn-muted">
                      {w.lastLocation?.siteName
                        ? `Nearest site: ${w.lastLocation.siteName}`
                        : 'No nearby work site'}
                      {' · '}
                      {formatClock(w.lastLocation?.capturedAt)}
                    </span>
                  </span>
                  <span className="tc-row-side">
                    <Pill variant="unsure" dot>
                      Outside a geofence
                    </Pill>
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      </ListState>
    </section>
  );
}

export { formatDistance };
